import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import formatDate from 'lib/date/formatDate.js';
import { formatToPhoneNumber } from 'lib/numUtils.js';
import validateEmail from 'lib/string/validateEmail.js';
import { validatePassword } from 'lib/string/validatePassword.js';
import { action } from 'lib/actionHelpers.js';
import SVG from 'images/svg/SVG';
import { inputHelpDescriptionMap } from 'lib/utilities';

const stableObject = {};
const stableArray = [];

const requiredMessages = {
  email: 'Please enter an email address.',
};

const validationMessages = {
  email: 'Please enter another email address.',
};

const formatValue = (value, format) => {
  if (typeof format === 'function') {
    value = format(value);
  }
  if (format === 'url') {
    value = String(value).replace(/[\s]/g, '');
  }
  if (format === 'date') {
    value = formatDate(value);
  }
  if (format === 'phone') {
    value = formatToPhoneNumber(value);
  }
  return value;
};

const BoundInput = React.memo(({ formId, id, placeholder, format, value, required, disabled, autoFocus, type, autoComplete, onChange, onBlur, onFocus, onKeyPress, onKeyUp, onKeyDown, disablePasswordValidation }) => {
  const reduxValue = useSelector((state) => ((state.ui.boundForms[formId] || stableObject).state || stableObject)[id]);
  const validating = useSelector((state) => (state.ui.boundForms[formId] || stableObject).validating);
  const reset = useSelector((state) => (state.ui.boundForms[formId] || stableObject).reset);
  const invalid = useSelector((state) => ((state.ui.boundForms[formId] || stableObject).invalids || stableArray).find((n) => n.id === id));
  const empty = useSelector((state) => ((state.ui.boundForms[formId] || stableObject).empties || stableArray).find((n) => n === id));
  const inputRef = useRef(null);
  const formGroupRef = useRef(null);
  const [showEmptyErrors, setShowEmptyErrors] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const valid = invalid === undefined;
  let displayValue = formId !== undefined ? reduxValue : value;
  const requiredMessage = requiredMessages[format] || `Please enter a ${placeholder || 'value'}.`;
  const invalidMessage = (invalid || {}).message || validationMessages[format] || 'Please enter another value.';

  useEffect(() => {
    if (invalid && formGroupRef.current) {
      formGroupRef.current.classList.add('shake-horizontal');
      setTimeout(() => {
        if (formGroupRef.current) {
          formGroupRef.current.classList.remove('shake-horizontal');
        }
      }, 1200);
    }
  }, [invalid]);

  useEffect(() => {
    if (type === 'password' && id !== 'confirmPassword' && displayValue && disablePasswordValidation !== true) {
      validatePassword({ formId, id, value: displayValue });
    }
  }, [displayValue]);

  const setValid = (valid) => {
    action(!valid ? 'uiSetFormInvalidField' : 'uiRemoveFormInvalidField', {
      formId,
      fieldId: id,
    });
  };

  const setEmpty = (empty) => {
    action(empty ? 'uiSetFormEmptyField' : 'uiRemoveFormEmptyField', {
      formId,
      fieldId: id,
    });
  };

  const validate = (opts = {}) => {
    let valid = true;
    const empty = !reduxValue && required;
    setEmpty(empty);
    if (empty) {
      if (opts.soft !== true) {
        setShowEmptyErrors(true);
      }
      return;
    }
    if (format === 'email' && reduxValue) {
      valid = validateEmail(reduxValue);
    }

    if (type === 'password' && id !== 'confirmPassword' && reduxValue && disablePasswordValidation !== true) {
      valid = validatePassword({ formId, id, value: reduxValue, returnBool: true });
    }
    setValid(valid);
  };

  const handleChange = (e) => {
    let value = formatValue(e.target.value, format);
    if (formId && id) {
      action('uiPatchFormState', {
        formId: formId,
        state: { [id]: value },
      });
    }

    if (typeof onChange === 'function') {
      onChange(e);
    }
    if (type === 'password' && id !== 'confirmPassword' && disablePasswordValidation !== true) {
      validatePassword({ formId, id, value });
    } else {
      setValid(true);
      setShowEmptyErrors(false);
    }
  };

  const handleBlur = (e) => {
    validate({ soft: true });
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  useEffect(() => {
    if (validating === true) {
      validate();
    }
  }, [validating]);

  useEffect(() => {
    validate({ soft: true });
  }, [required]);

  useEffect(() => {
    validate({ soft: true });
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [reset]);

  const showErrors = !valid || (showEmptyErrors && empty);
  return (
    <Form.Group ref={formGroupRef}>
      <Form.Label className={scrollTop !== 0 ? 'hidden' : ''} column={false}>
        {placeholder || ''}
        {inputHelpDescriptionMap[placeholder] ? <SVG icon={'Help'} title={inputHelpDescriptionMap[placeholder]} /> : null}
      </Form.Label>
      <Form.Control ref={inputRef} name={id} autoFocus={autoFocus} autoComplete={autoComplete} onBlur={handleBlur} onFocus={onFocus} disabled={disabled} as={type === 'textarea' ? 'textarea' : undefined} type={type} placeholder={undefined} spellCheck={type === 'textarea'} onChange={handleChange} onScroll={type === 'textarea' ? handleScroll : undefined} onKeyPress={onKeyPress} onKeyDown={onKeyDown} onKeyUp={onKeyUp} value={displayValue || ''} />
      {showErrors && <div className="invalid-feedback">{!displayValue ? requiredMessage : invalidMessage}</div>}
    </Form.Group>
  );
});

export default BoundInput;
